import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { playNotificationSound, getPayload } from 'utils/playNotification';
import { useToast, NotificationToast } from 'components';
import {
  setNotificationMuted,
  setNotificationVolume,
  addNotification,
} from 'store/slices/globalSlice';
import useWebSocket from './useWebSocket';
import { SESSION_KEYS } from 'utils/constant';
import { MSG_TYPE } from 'api/services/websocketService';

// Sanitize strings to prevent XSS attacks
const sanitize = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(
    /[<>&"']/g,
    (char) =>
      ({
        '<': '&lt;',
        '>': '&gt;',
        '&': '&amp;',
        '"': '&quot;',
        "'": '&#39;',
      }[char])
  );
};

// Validate image URLs to prevent javascript: or malicious data URIs
const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

export const useGlobalNotificationListener = () => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const { showToast } = useToast();

  // Sync volume/muted state from session storage on mount
  useEffect(() => {
    try {
      const storedVolume = sessionStorage.getItem(SESSION_KEYS.VOLUME);
      const storedMuted = sessionStorage.getItem(SESSION_KEYS.MUTED);

      if (storedVolume !== null && storedVolume !== '') {
        const parsed = Number(storedVolume);
        if (!Number.isNaN(parsed)) {
          dispatch(setNotificationVolume(parsed));
        }
      }

      if (storedMuted !== null && storedMuted !== '') {
        dispatch(setNotificationMuted(storedMuted === 'true'));
      }
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, [dispatch]);

  const onMessageHandler = useCallback(
    (message) => {
      const payload = getPayload(message);
      if (payload && payload.id && payload.type === 'proposal') {
        // Sanitize payload before storing in Redux to prevent XSS
        dispatch(
          addNotification({
            ...payload,
            company: sanitize(payload.company),
            title: sanitize(payload.title),
            description: sanitize(payload.description),
          })
        );
      }

      // Play notification sound for incoming messages (not our own)
      if (message.type === MSG_TYPE.MESSAGE) {
        // Play sound if we can't determine current user OR if it's from another user
        const isOwnMessage =
          currentUserId != null && message.payload?.sender_id === currentUserId;
        if (!isOwnMessage) {
          playNotificationSound(message);
          if (payload?.type === 'proposal') {
            // Use sanitized values for toast (payload is raw, not from Redux)
            showToast(
              <NotificationToast
                title={sanitize(payload.company) || 'Unknown Company'}
                subtitle={sanitize(payload.title) || 'New proposal'}
                avatar={isValidImageUrl(payload.logo) ? payload.logo : ''}
                status={payload.status}
              />,
              5000
            );
          }
        }
      }
    },
    [dispatch, currentUserId, showToast]
  );

  const { isConnected } = useWebSocket({
    onMessage: onMessageHandler,
  });

  return { isConnected };
};
